import { PaymentData } from '@/state/payment'
import { PaymentMethodInputTypes } from '@/utils/constants'
import { formatPaymentUserName } from '@/utils/helpers'

export interface FortisValidationResult {
  valid: boolean
  errorDescription: string
}

/**
 * Validates a Fortis fortisDone event payload.
 * Checks AVS, CVV, and status_code in order.
 * Returns { valid: true } on success, or { valid: false, errorDescription } on failure.
 */
export function validateFortisEvent(event: {
  avs?: string
  avs_enhanced?: string
  cvv_response?: string
  status_code?: string | number
}): FortisValidationResult {
  const isAvsValid = event.avs === 'GOOD' || event.avs === 'STREET'

  if (!isAvsValid) {
    const validEnhanced = ['G', 'I', 'S', 'V']
    if (!event.avs_enhanced || !validEnhanced.includes(event.avs_enhanced)) {
      return { valid: false, errorDescription: 'Invalid Zip Code' }
    }
  }

  if (event.cvv_response !== 'M') {
    return { valid: false, errorDescription: 'Invalid CVV' }
  }

  // eslint-disable-next-line eqeqeq
  if (event.status_code != '121') {
    return { valid: false, errorDescription: 'Invalid Card' }
  }

  return { valid: true, errorDescription: '' }
}

/**
 * Converts a Fortis exp_date string (MMYY, e.g. "1225") to YYYYMM format (e.g. "202512").
 * Fortis provides exp_date as a 4-char string where index [0][1] = MM and [2][3] = YY.
 */
export function buildFortisExpiration(expDate: string): string {
  return `20${expDate[2]}${expDate[3]}${expDate[0]}${expDate[1]}`
}

/**
 * Constructs a PaymentData object from a Fortis fortisDone event data payload.
 */
export function buildFortisPaymentMethod(data: {
  account_type: string
  account_holder_name: string
  last_four: string
  account_vault_id: string
  saved_account: { exp_date: string }
}): PaymentData {
  return {
    network: data.account_type,
    name: formatPaymentUserName(data.account_holder_name),
    pan_mask: `************${data.last_four}`,
    input_mode: PaymentMethodInputTypes.FORTIS_ECOMMERCE_MANUAL_KEYED,
    card_token: data.account_vault_id,
    expiration: buildFortisExpiration(data.saved_account.exp_date),
  }
}

/**
 * Deduplicates a payment methods list when adding a new payment method.
 *
 * - If the first entry has an empty card_token (placeholder), it is replaced.
 * - If an existing entry matches, it is updated in place.
 * - Otherwise the new method is appended.
 *
 * @param existing - Current list of payment methods
 * @param incoming - The new payment method to add
 * @param matchFn - Function that returns true when an existing entry is considered a duplicate
 */
export function deduplicatePaymentMethods(
  existing: PaymentData[],
  incoming: PaymentData,
  matchFn: (existing: PaymentData, incoming: PaymentData) => boolean
): PaymentData[] {
  if (existing[0]?.card_token === '') {
    return [...existing.slice(1), incoming]
  }

  const existingIndex = existing.findIndex((pm) => matchFn(pm, incoming))

  if (existingIndex !== -1) {
    const updated = [...existing]
    updated[existingIndex] = incoming
    return updated
  }

  return [...existing, incoming]
}

/** Match function for Fortis: duplicate = same pan_mask AND same network */
export function fortisDuplicateMatch(a: PaymentData, b: PaymentData): boolean {
  return a.pan_mask === b.pan_mask && a.network === b.network
}

/** Match function for Magensa: duplicate = same pan_mask only */
export function magensaDuplicateMatch(a: PaymentData, b: PaymentData): boolean {
  return a.pan_mask === b.pan_mask
}
